import { moduleActionContext } from "@/store";
import { defineModule } from "direct-vuex";
import { FILTER_NAMES } from "@/static/filterNames";
import _ from "lodash";
import UserCreditCard, { UserCreditCardResponse } from "@/models/api/administration/bankAccounts/UserCreditCard";
import {
  getUserCreditCards,
  postAssignCardToUser,
  postToggleDefaultCreditCard,
  postUnassignCardFromUser,
} from "@/requests/administration/administrationCreditCards";
import { DataOptions } from "vuetify";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { getUsersTable } from "@/requests/administration/administrationUsers";
import { User } from "@/models/api/administration/users/User";

export interface AdministrationUserCreditCardsStateInterface {
  users: Array<User>;
  userFilters: null | Array<CriterionBase>;
  userLoading: boolean;
  selectedUserId: string | null;
  creditCards: Array<UserCreditCard>;
  creditCardCount: number | null;
  creditCardFilters: null | Array<CriterionBase>;
  creditCardOptions: DataOptions;
  creditCardLoading: boolean;
}

export const AdministrationUserCreditCardsState: AdministrationUserCreditCardsStateInterface = {
  users: [],
  userFilters: null,
  userLoading: false,
  selectedUserId: null,
  creditCards: [],
  creditCardCount: null,
  creditCardFilters: null,
  creditCardOptions: new DefaultDataOptions(),
  creditCardLoading: false,
};

const AdministrationUserCreditCardsModule = defineModule({
  state: AdministrationUserCreditCardsState as AdministrationUserCreditCardsStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setUsers(state, param: Array<User>) {
      state.users = param;
    },
    setUserFilters(state, param: Array<CriterionBase>) {
      state.userFilters = param;
    },
    setUserLoading(state, param: boolean) {
      state.userLoading = param;
    },
    setSelectedUserId(state, param: string | null) {
      state.selectedUserId = param;
    },
    setCreditCards(state, param: UserCreditCardResponse) {
      state.creditCards = param.items;
      state.creditCardCount = param.totalCount;
    },
    setCreditCardFilters(state, param: Array<CriterionBase>) {
      state.creditCardFilters = param;
    },
    setCreditCardOptions(state, param: DataOptions) {
      state.creditCardOptions = param;
    },
    setCreditCardLoading(state, param: boolean) {
      state.creditCardLoading = param;
    },
  },
  actions: {
    async downloadUsers(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setUserLoading(true);
      let users = await getUsersTable({
        count: 0,
        page: 0,
        criterions: _.cloneDeep(state.userFilters),
        filterName: FILTER_NAMES.UserFilter,
      });
      users = users.sort((a, b) => a.userName.localeCompare(b.userName));
      commit.setUsers(users);
      if (users.length > 0) {
        commit.setSelectedUserId(users[0].id);
      }
      commit.setUserLoading(false);
    },
    async downloadUserCreditCards(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedUserId) {
        commit.setCreditCardLoading(true);
        const creditCards = await getUserCreditCards(
          {
            count: state.creditCardOptions.itemsPerPage,
            page: state.creditCardOptions.page - 1,
            criterions: _.cloneDeep(state.creditCardFilters),
            filterName: FILTER_NAMES.CreditCardsFilter,
          },
          state.selectedUserId
        );
        commit.setCreditCards(creditCards);
        commit.setCreditCardLoading(false);
      }
    },
    async assignCardToUser(context, creditCardId: number) {
      const { state } = _moduleActionContext(context);
      if (state.selectedUserId) {
        await postAssignCardToUser(state.selectedUserId, creditCardId);
      }
    },
    async unassignCardFromUser(context, creditCardId: number) {
      const { state } = _moduleActionContext(context);
      if (state.selectedUserId) {
        await postUnassignCardFromUser(state.selectedUserId, creditCardId);
      }
    },
    async toggleDefaultCreditCard(context, creditCardId: number) {
      const { state } = _moduleActionContext(context);
      if (state.selectedUserId) {
        await postToggleDefaultCreditCard(state.selectedUserId, creditCardId);
      }
    },
  },
});

export default AdministrationUserCreditCardsModule;
// eslint-disable-next-line
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationUserCreditCardsModule);
